import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, Check } from 'lucide-react';
import useSEO from '@/hooks/useSEO';

const Register = () => {
  useSEO({ title: 'Create account — EpisodeKit', path: '/register', index: false });
  const navigate = useNavigate();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await register(formData.email, formData.password, formData.name);
      toast.success('Account created! Check your email to verify.');
      navigate('/verify-email');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    '1 free episode per month',
    'AI-powered transcription',
    'Show notes & blog posts',
    'Social media snippets'
  ];

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6" data-testid="register-page">
      <div className="noise-overlay" />
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center justify-center mb-8">
            <img 
              src="/logo-full.png" 
              alt="EpisodeKit" 
              className="h-10 w-auto"
            />
          </Link>
          <h1 className="text-3xl font-unbounded font-bold mb-2">Create Account</h1>
          <p className="text-neutral-400">Start your free trial today</p>
        </div>

        <div className="card-base">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                type="text"
                placeholder="John Doe"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="input-base w-full"
                required
                data-testid="name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="input-base w-full"
                required
                data-testid="email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="input-base w-full pr-10"
                  required
                  minLength={6}
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="bg-neutral-900/50 rounded-lg p-4">
              <p className="text-sm font-medium mb-2">Free trial includes:</p>
              <ul className="space-y-1">
                {benefits.map((benefit) => (
                  <li key={benefit} className="flex items-center gap-2 text-sm text-neutral-400">
                    <Check className="w-4 h-4 text-success" />
                    {benefit}
                  </li>
                ))}
              </ul>
              <div className="mt-3 pt-3 border-t border-neutral-800">
                <p className="text-sm font-medium text-orange-400 flex items-center gap-2">
                  <span className="text-base">🔥</span>
                  No credit card required
                </p>
              </div>
            </div>

            <Button
              type="submit"
              className="btn-primary w-full"
              disabled={loading}
              data-testid="register-submit-btn"
            >
              {loading ? <div className="spinner w-5 h-5" /> : 'Create Account'}
            </Button>
          </form>
        </div>

        <p className="text-center mt-6 text-neutral-400">
          Already have an account?{' '}
          <Link to="/login" className="text-primary hover:underline" data-testid="login-link">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Register;
